import { ref, computed } from "vue";
import type { Ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { ERROR_CODE } from "@plugins/common/constant/error.constant";

export interface ApiError {
  response?: {
    status?: number;
    data?: {
      errorMap?: Record<string, string>;
    };
  };
}

interface ProcessingStore {
  reset(anchor?: string): void;
}

export function useErrorHandler(
  options: {
    processingStore?: ProcessingStore;
    anchor?: string;
    isProcessing?: Ref<boolean>;
  } = {},
) {
  const { t } = useI18n();
  const store = useStore();
  const router = useRouter();

  const error = ref({
    code: "",
    message: "",
    subMessage: "",
  });

  const hasError = computed(() => error.value.message !== "");

  const isProcessing: Ref<boolean> = options.isProcessing ?? ref(false);

  function clearErrorState() {
    error.value.code = "";
    error.value.message = "";
    error.value.subMessage = "";
  }

  function handleCloseError() {
    clearErrorState();
  }

  function actionApiFailHandler() {
    error.value.message = t("api_action_generic_error_title");
    error.value.subMessage = t("api_action_generic_error_subtitle");
    if (options.processingStore) {
      options.processingStore.reset(options.anchor);
    } else {
      isProcessing.value = false;
    }
  }

  function passiveApiFailHandler(err?: unknown) {
    const apiError = err as ApiError;
    const rawStatus = apiError?.response?.status;
    const errorStatus = rawStatus ? rawStatus : null;
    const errorMap = apiError?.response?.data?.errorMap;
    const firstEntry = Array.isArray(errorMap) ? errorMap[0] : errorMap?.["0"];
    const errorCode = errorStatus === 500 && firstEntry ? firstEntry : "";
    if (errorCode === ERROR_CODE.REQUEST_BLOCKED) return;

    store.dispatch("app/addError", {
      subTitle: errorCode,
      hasTimeout: !errorCode,
      id: errorCode || "generic",
      code: errorCode,
      statusCode: errorStatus,
    });

    console.error("Unexpected error:", err);
  }

  function validateCommonErrorCode(response: object) {
    if (response["code"] !== "ok") {
      let needPassiveApiFailHandler = true;
      const errorCode = response["errorMap"]["0"];

      if (errorCode === ERROR_CODE.NOT_FOUND) {
        needPassiveApiFailHandler = false;
        router.push({ name: "PageNotFound" });
      } else if (errorCode === ERROR_CODE.ACCESS_DENIED) {
        needPassiveApiFailHandler = false;
        router.push({ name: "Forbidden" });
      }

      if (needPassiveApiFailHandler) {
        passiveApiFailHandler(response);
      }

      return false;
    }

    return true;
  }

  function isLessonUnpublishedError(response: object): boolean {
    if (response["code"] !== "ok") {
      const errorCode = response["errorMap"]?.["0"];
      return errorCode === ERROR_CODE.LESSON_UNPUBLISHED;
    }
    return false;
  }

  function addError(payload: unknown): unknown {
    return store.dispatch("app/addError", payload);
  }

  return {
    error,
    hasError,
    isProcessing,
    addError,
    clearErrorState,
    handleCloseError,
    actionApiFailHandler,
    passiveApiFailHandler,
    validateCommonErrorCode,
    isLessonUnpublishedError,
  };
}
